import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const EVENT_START = new Date('2026-06-03T08:00:00+08:00');
const EVENT_END = new Date('2026-06-05T18:00:00+08:00');

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function calcTimeLeft(): TimeLeft | null {
  const now = new Date();
  const diff = EVENT_START.getTime() - now.getTime();
  if (diff <= 0) return null;
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
}

type EventStatus = 'upcoming' | 'live' | 'ended';

function getStatus(): EventStatus {
  const now = new Date();
  if (now < EVENT_START) return 'upcoming';
  if (now <= EVENT_END) return 'live';
  return 'ended';
}

export default function EventCountdown() {
  const { t } = useTranslation();
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(calcTimeLeft);
  const [status, setStatus] = useState<EventStatus>(getStatus);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calcTimeLeft());
      setStatus(getStatus());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  if (status === 'live') {
    return (
      <div className="flex items-center justify-center gap-3 py-4">
        <span className="relative flex h-4 w-4">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-4 w-4 bg-red-500" />
        </span>
        <span className="text-2xl md:text-3xl font-bold text-white">{t('countdown.eventLive')}</span>
      </div>
    );
  }

  if (status === 'ended') {
    return (
      <div className="text-center py-4">
        <span className="text-xl md:text-2xl font-medium text-white/90">{t('countdown.eventEnded')}</span>
      </div>
    );
  }

  if (!timeLeft) return null;

  const units = [
    { value: timeLeft.days, label: t('countdown.days') },
    { value: timeLeft.hours, label: t('countdown.hours') },
    { value: timeLeft.minutes, label: t('countdown.minutes') },
    { value: timeLeft.seconds, label: t('countdown.seconds') },
  ];

  return (
    <div>
      <p className="text-white/80 text-sm md:text-base mb-3 text-center">{t('countdown.title')}</p>
      <div className="flex items-center justify-center gap-3 md:gap-4">
        {units.map((unit, i) => (
          <div key={i} className="flex flex-col items-center">
            <div className="countdown-card min-w-[60px] md:min-w-[80px]">
              <span className="text-2xl md:text-4xl font-bold tabular-nums">
                {String(unit.value).padStart(2, '0')}
              </span>
            </div>
            <span className="text-white/70 text-xs md:text-sm mt-1">{unit.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
